import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Box,
  Grid,
  Pagination,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import "./MapList.scss";
import { useNavigate, useParams } from "react-router-dom";
import { GetStakemapList } from "../../service/StakemapService";
import { PreferencesContext } from "../../PreferenceContext";
import { AlertType } from "../../shared/AlertType";
import { format } from "date-fns";
import CustomPagination from "../CustomPagination";
import CustomTooltip from "../CustomTooltip";

const MapList = ({
  title,
  setTitle,
}: {
  title: string;
  setTitle: (val: string) => void;
}) => {
  const navigate = useNavigate();
  // const { token } = useParams();
  const { userContext, updateUserContext } = useContext(PreferencesContext);
  // const token = userContext?.token;
  const { token: tokenFromParams } = useParams();
  const token = tokenFromParams || userContext?.token;
  const [isLoading, setLoading] = useState(false);
  const [param, setParam] = useState({
    pageIndex: 1,
    pageSize: 10,
    sortBy: "",
  });
  const [mapList, setMapList] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const handleClickToken = (link: string, stakemapUserId: any) => {
    updateUserContext({
      ...userContext,
      token: link,
      stakemapUserId: stakemapUserId,
    });
    navigate(`/${link}`);
  };
  const fetchStakemapList = async (
    sortBy: string = "",
    pageIndex: number = 1,
    pageSize: number = 10
  ) => {
    setLoading(true);
    try {
      let response: any;
      let params = new URLSearchParams();
      //   params.append("token", token || "");
      params.append("SORT_BY", sortBy);
      params.append("pageIndex", pageIndex.toString());
      params.append("pageSize", pageSize.toString());
      response = await GetStakemapList(params);
      if (response?.data?.data?.data) {
        setMapList(response.data?.data?.data);
        setTotalCount(response.data.data.total_data_count || 0);
      }
    } catch (error: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage: error.message,
        alertType: AlertType.Error,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStakemapList(param.sortBy, param.pageIndex, param.pageSize);
  }, [param.pageIndex, param.pageSize, param.sortBy]);
  useEffect(() => {
    if (userContext?.userRole === "ADMIN" && userContext?.refreshStakeMapList) {
      fetchStakemapList();
    }
  }, [userContext?.refreshStakeMapList]);

  return (
    <Box>
      <TableContainer
        component={Paper}
        sx={{
          // marginBottom: "40px",
          boxShadow: "none",
          width: "100%",
          // height: "100%",
          height: "85vh",
          padding: "30px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Table
          sx={{
            borderCollapse: "separate",
            borderSpacing: 0,
            width: "100%",
            "& td, & th": {
              border: "none",
            },
            "& thead th": {
              borderBottom: "1px solid #E9E9E9",
            },
          }}
        >
          {mapList?.length > 0 && (
            <TableHead>
              <TableRow sx={{ fontSize: "16px" }}>
                <TableCell
                  className="map-table-header"
                  sx={{ paddingLeft: "16px", width: "24%" }}
                >
                  Stakeholder Map
                </TableCell>
                <TableCell
                  className="map-table-header"
                  sx={{ paddingLeft: "16px", width: "23%" }}
                >
                  Organization Name
                </TableCell>
                <TableCell
                  className="map-table-header"
                  sx={{ paddingLeft: "16px", width: "23%" }}
                >
                  Created By
                </TableCell>
                <TableCell
                  className="map-table-header"
                  sx={{ paddingLeft: "16px", width: "15%" }}
                >
                  Created Date
                </TableCell>
                <TableCell
                  className="map-table-header"
                  sx={{ paddingLeft: "16px", width: "15%" }}
                >
                  Last Accessed Date
                </TableCell>
              </TableRow>
            </TableHead>
          )}
        </Table>
        <Box sx={{ flex: 1, overflowY: "auto" }}>
          <Table>
            <TableBody>
              {isLoading
                ? Array.from({ length: 4 }, (_, i) => (
                    <TableRow key={i}>
                      <TableCell colSpan={6}>
                        <Skeleton variant="rounded" width="100%" height={15} />
                      </TableCell>
                    </TableRow>
                  ))
                : mapList?.length > 0 &&
                  mapList.map((map, index) => (
                    <TableRow key={index} sx={{ height: "50px" }}>
                      {/* <TableCell className="tabel-data">{map.LINK}</TableCell> */}
                      <TableCell
                        className="tabel-data"
                        sx={{
                          cursor: "pointer",
                          color: "black",
                          maxWidth: "200px", // optional: limits the cell width for ellipsis
                          ":hover": {
                            color: "blue",
                            paddingLeft: "16px", // optional slight shift
                          },
                        }}
                        onClick={() =>
                          handleClickToken(map.LINK, map?.CREATED_BY)
                        }
                      >
                        <CustomTooltip text={map.TITLE || ""}>
                          <span
                            style={{
                              display: "inline-block",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              width: "100%",
                              maxHeight: "24px", // match row height
                              verticalAlign: "middle",
                            }}
                          >
                            {map.TITLE}
                          </span>
                        </CustomTooltip>
                      </TableCell>

                      <TableCell
                        className="tabel-data"
                        sx={{ paddingLeft: "16px", width: "23%" }}
                      >
                        {map?.createdBy?.ORGANIZATION_NAME}
                      </TableCell>
                      <TableCell
                        className="tabel-data"
                        sx={{ paddingLeft: "16px", width: "23%" }}
                      >
                        {map?.createdBy?.FIRST_NAME}
                      </TableCell>

                      <TableCell
                        className="tabel-data"
                        sx={{ paddingLeft: "16px", width: "15%" }}
                      >
                        {map.CREATED_DATE
                          ? format(
                              new Date(map.CREATED_DATE),
                              "dd-MM-yyyy HH:mm aa"
                            )
                          : ""}
                      </TableCell>
                      <TableCell
                        className="tabel-data"
                        sx={{ paddingLeft: "16px", width: "15%" }}
                      >
                        {map.CREATED_DATE
                          ? format(
                              new Date(map.ACCESSED_DATE),
                              "dd-MM-yyyy HH:mm aa"
                            )
                          : ""}
                      </TableCell>
                    </TableRow>
                  ))}
            </TableBody>
          </Table>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            paddingTop: "8px",
          }}
        >
          {mapList.length > 0 && (
            <CustomPagination
              param={param}
              setParam={setParam}
              totalCount={totalCount}
            />
          )}
        </Box>
        {/* <Box sx={{ paddingBottom: "0 !important" }}>
          {mapList.length > 0 && (
            <CustomPagination
              param={param}
              setParam={setParam}
              totalCount={totalCount}
            />
          )}
        </Box> */}
      </TableContainer>
    </Box>
  );
};

export default MapList;
